import type {ValueType} from '@github-ui/custom-properties-types'

const MAX_PROPERTY_NAME_LENGTH = 75
const MAX_PROPERTY_DESCRIPTION_LENGTH = 255

const NAME_INVALID_CHARS_REGEX = /[^a-zA-Z0-9_#$-]/gu
const VALUE_INVALID_CHARS_REGEX = /[^ -!#-~]/gu

export function validatePropertyName(value: string, existingPropertyNames: string[]): string | undefined {
  if (value.length > MAX_PROPERTY_NAME_LENGTH) {
    return `Name cannot be longer than ${MAX_PROPERTY_NAME_LENGTH} characters`
  } else if (value.length === 0) {
    return 'Name is required'
  } else {
    for (const name of existingPropertyNames) {
      if (name.toLowerCase() === value.toLowerCase()) {
        return 'Name already exists'
      }
    }
  }

  const invalidChars = findChars(value, NAME_INVALID_CHARS_REGEX)
  if (invalidChars) {
    return `Name contains invalid characters: ${invalidChars}`
  }
}

export function validateAllowedValue(value: string, existingAllowedValues: Set<string>): string | undefined {
  if (value.length > MAX_PROPERTY_NAME_LENGTH) {
    return `Option cannot be longer than ${MAX_PROPERTY_NAME_LENGTH} characters`
  } else if (value.length === 0) {
    return 'Option cannot be empty'
  } else if (existingAllowedValues.has(value)) {
    return 'Option already exists'
  }

  const invalidChars = findChars(value, VALUE_INVALID_CHARS_REGEX)
  if (invalidChars) {
    return `Option contains invalid characters: ${invalidChars}`
  }
}

export function validatePropertyDescription(value: string): string | undefined {
  if (value.length > MAX_PROPERTY_DESCRIPTION_LENGTH) {
    return `Description cannot be longer than ${MAX_PROPERTY_DESCRIPTION_LENGTH} characters`
  }
}

export function validatePropertyDefaultValue(
  value: string,
  valueType: ValueType,
  isRequired: boolean,
  allowedValues: string[],
): string | undefined {
  if (!isRequired) {
    return
  }
  if (valueType === 'single_select' && allowedValues.length > 0 && !allowedValues.includes(value)) {
    return 'Cannot be empty for a required property'
  }

  if (!value) {
    return 'Cannot be empty for a required property'
  } else {
    return validateValue(value)
  }
}

export function validateValue(value: string): string | undefined {
  if (!value) {
    return
  }

  if (value.length > MAX_PROPERTY_NAME_LENGTH) {
    return `Cannot be longer than ${MAX_PROPERTY_NAME_LENGTH} characters`
  }

  const invalidChars = findChars(value, VALUE_INVALID_CHARS_REGEX)
  if (invalidChars) {
    return `Contains invalid characters: ${invalidChars}`
  }
}

function findChars(value: string, regex: RegExp) {
  const chars = Array.from(value.matchAll(regex))
  if (!chars) {
    return ''
  }

  const unique = new Set(chars.map(c => (c[0] === ' ' ? 'whitespace' : c[0])))
  return Array.from(unique).join(', ')
}
